import React, { useState, useEffect } from 'react'
import { useDispatch, connect } from 'react-redux'
import RootState from 'typings/RootState'
import styled from 'styled-components'
import { RouteComponentProps } from 'react-router-dom'
import { BreadcrumbLink } from 'Components/Shared/BreadCrumb/BreadCrumb'
import SubnavBar from 'Components/Shared/SubnavBar'
import { HeaderRow } from 'Components/Shared/Table'
import Pagination from 'Components/Shared/Pagination'
import { PaginationType } from 'features/types/common'
import {
  userEmailSelector,
  serviceTokenSelector,
} from 'features/sensitive/sensitive.selector'
import {
  getReportList,
  exportReportList,
} from 'features/adminReport/performance/api'
import { sortingTask } from 'features/adminReport/performance/actions'
import {
  GetReportListReq,
  DataItem,
} from 'features/adminReport/performance/types'
import {
  selectEvent,
  selectFilter,
  selectReportList,
  selectSorting,
  selectInitFilter,
} from 'features/adminReport/performance/selector'
import Menu from 'Components/MenuAdmin/AdminQCMenu'
import RowItem from './RowItem'
import { EventModal } from 'Components/Shared'
import Filter from 'Components/AdminQC/Report/Filter/PerformanceFilter'
import { toDateText } from 'utils/datetime'
import { initPagination } from 'utils/helper'

const breadcrumbs: BreadcrumbLink[] = [
  {
    url: '/dashboard',
    title: 'Home',
    icon: '/home.png',
    active: true,
    showTitle: false,
  },
  {
    url: '/report/performance',
    title: 'รายงาน Performance',
    active: false,
    showTitle: true,
  },
]

type Props = ReduxProps & DispatchProps & RouteComponentProps

const ReportList: React.ComponentType<Props> = ({
  UserEmail,
  token,
  dataList,
  event,
  filter,
  sorting,
  filter: currentFilter,
  initFilter,
  dataExport,
  exportEvent,
}) => {
  const dispatch = useDispatch()
  const [enableModal, setEnableModal] = useState(false)
  const [pagination, setPagination] = useState<PaginationType>(initPagination())

  useEffect(() => {
    onFindReport()
  }, [pagination])

  const onFindReport = (
    isClearFilter: boolean = false,
    isExport: boolean = false
  ) => {
    setEnableModal(true)
    const filter = isClearFilter ? initFilter : currentFilter
    const req: GetReportListReq = {
      UserEmail,
      StartDate: toDateText(filter.Date.StartDate || ''),
      EndDate: toDateText(filter.Date.FinishDate || ''),
      UserID: filter.UserID,
      UserRoleID: filter.UserRoleID,
      Page: isExport ? 1 : pagination.Page,
      RowPerPage: isExport ? dataList.Total : pagination.RowPerPage,
    }

    if (isExport) {
      dispatch(exportReportList(token, req))
    } else {
      dispatch(getReportList(token, req))
    }
  }

  const onSuccess = () => {
    setEnableModal(false)
  }

  const onSort = (name: string) => {
    dispatch(sortingTask({ name }))
  }

  const content = dataList.List.map((item: DataItem, index: number) => {
    return <RowItem key={index} item={item} />
  })

  return (
    <>
      <SubnavBar breadcrumbs={breadcrumbs} hideSearch={true} />
      <EventModal
        name="report-constructplan"
        event={[event, exportEvent]}
        enable={enableModal}
        successCalback={onSuccess}
      />
      <Menu selected={'report.performance'}>
        <MainPage>
          <Header>รายงาน Performance</Header>
          <Filter
            onSearch={(isClear: boolean = false) => {
              pagination.Page = 1
              setPagination({ ...pagination })
              onFindReport(isClear)
            }}
            isDownLoading={exportEvent.IsLoading}
            onExport={() => onFindReport(false, true)}
            exportData={dataExport}
          />
          <HeaderRow
            onSort={name => onSort(name)}
            sorting={sorting}
            items={columnsName}
          />
          {content}
          <Pagination
            currentPage={pagination.Page}
            totalRows={dataList.Total}
            perPage={pagination.RowPerPage}
            onChangeNumber={(page: number) => {
              pagination.RowPerPage = page
              setPagination({ ...pagination })
            }}
            onClickPage={(page: number) => {
              pagination.Page = page
              setPagination({ ...pagination })
            }}
          />
        </MainPage>
      </Menu>
    </>
  )
}

const MainPage = styled.div`
  padding: 40px 45px;
  width: 100%;
`

const Header = styled.div`
  margin-bottom: 27px;
  font-size: 28px;
  line-height: 1.07;
  font-family: 'DB Helvethaica X 75 Bd';
  color: #000;
`

const columnsName = [
  {
    title: 'กลุ่ม',
    span: 3,
    key: 'UserRoleName',
  },
  {
    title: 'ชื่อผู้ตรวจ',
    span: 3,
    key: 'UserName',
  },
  {
    title: 'จำนวนวัน',
    span: 2,
    key: 'NumOfDate',
  },
  {
    title: 'ตรวจมาตรฐาน',
    span: 2,
    key: 'NumOfUnit',
  },
  {
    title: 'ส่วนกลาง',
    span: 2,
    key: 'NumOfZone',
  },
  {
    title: 'Commissining',
    span: 2,
    key: 'NumOfCommissioning',
  },
  {
    title: 'Safety',
    span: 2,
    key: 'NumOfSafety',
  },
  {
    title: 'Site visit ระหว่างก่อสร้าง',
    span: 2,
    key: 'NumOfConstructPlan',
  },
  {
    title: 'เปรียบเทียบแบบจัดสรร',
    span: 2,
    key: 'NumOfAuditPlan',
  },
  {
    title: 'ถนนระหว่างก่อสร้าง',
    span: 2,
    key: 'NumOfConstructRoad',
  },
  {
    title: 'ตรวจรับมอบถนนและก่อนหมดประกัน',
    span: 2,
    key: 'NumOfAuditRoad',
  },
]

const mapStateToProps = (state: RootState) => {
  return {
    dataList: selectReportList(state),
    event: selectEvent(state),
    filter: selectFilter(state),
    initFilter: selectInitFilter(state),
    sorting: selectSorting(state),
    UserEmail: userEmailSelector(state),
    token: serviceTokenSelector(state),
    dataExport: state.adminReportPerformance.DataExport,
    exportEvent: state.adminReportPerformance.ExportEvent,
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>
const mapDispatchToProps = {}

type DispatchProps = typeof mapDispatchToProps

export default connect(mapStateToProps, mapDispatchToProps)(ReportList)
